// LineDonutChart.tsx
// Gráfico de donut para concentração por linha de produto

import React, { useRef } from 'react';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip
} from 'recharts';

interface DonutData {
    name: string;
    value: number;
    color?: string;
}

interface LineDonutChartProps {
    data: DonutData[];
    totalLabel?: string;
    selectedSegment?: string | null;
    onSegmentClick?: (name: string) => void;
}

const DEFAULT_COLORS = [
    'var(--theme-primary, #d97706)',
    'var(--theme-accent, #1e293b)',
    '#334155',
    '#475569',
    '#64748b',
    '#94a3b8',
    '#cbd5e1',
    '#e2e8f0'
];

// Renderer de label com linha guia (evita colisão)
const makePieLabelRenderer = () => {
    let occupiedAngles: number[] = [];
    const MIN_GAP_DEG = 20;

    return (props: any) => {
        const { cx, cy, midAngle, innerRadius, outerRadius, percent, name, payload, index } = props;
        if (index === 0) occupiedAngles = [];
        if ((percent || 0) < 0.03) return null;

        const angle = ((midAngle % 360) + 360) % 360;
        const conflict = occupiedAngles.some((used: number) => {
            const diff = Math.abs(angle - used);
            return Math.min(diff, 360 - diff) < MIN_GAP_DEG;
        });
        if (conflict) return null;
        occupiedAngles.push(angle);

        const RADIAN = Math.PI / 180;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 8) * cos;
        const sy = cy + (outerRadius + 8) * sin;
        const mx = cx + (outerRadius + 26) * cos;
        const my = cy + (outerRadius + 26) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 10;
        const ey = my;
        const textAnchor = cos >= 0 ? 'start' : 'end';
        const pct = ((percent || 0) * 100).toFixed(1);

        return (
            <g>
                <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={payload.color || 'var(--text-secondary)'} fill="none" strokeWidth={1.5} />
                <circle cx={ex} cy={ey} r={2} fill={payload.color || 'var(--text-secondary)'} stroke="none" />
                <text
                    x={ex + (cos >= 0 ? 1 : -1) * 6}
                    y={ey}
                    textAnchor={textAnchor}
                    fill="var(--text-primary)"
                    fontSize={11}
                    fontWeight={700}
                    style={{ textTransform: 'uppercase' }}
                >
                    {name.length > 18 ? name.substring(0, 17) + '…' : name}
                    <tspan x={ex + (cos >= 0 ? 1 : -1) * 6} dy={12} fill="var(--text-primary)" fontWeight={600} fontSize={12}>
                        {pct}%
                        <tspan fill="var(--text-secondary)" fontWeight={500} fontSize={11}>
                            {` | ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(payload.value)}`}
                        </tspan>
                    </tspan>
                </text>
            </g>
        );
    };
};

const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div style={{
            background: 'var(--bg-widget)',
            borderRadius: 10,
            padding: '10px 14px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            border: '1px solid var(--border-primary)',
            fontSize: 12
        }}>
            <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', fontSize: 13 }}>
                {d.name}
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>Valor</span>
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(d.value)}
                </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 2 }}>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>%</span>
                <span style={{ fontWeight: 700, color: 'var(--theme-primary, #d97706)' }}>
                    {((d.value / (payload[0]?.payload?.total || 1)) * 100).toFixed(1)}%
                </span>
            </div>
        </div>
    );
};

const LineDonutChart: React.FC<LineDonutChartProps> = ({ data, totalLabel = 'Total', selectedSegment, onSegmentClick }) => {
    const labelRenderer = useRef(makePieLabelRenderer()).current;
    const hasSelection = !!selectedSegment;

    if (!data || data.length === 0) {
        return (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary, #888)', fontWeight: 500, fontSize: 13 }}>
                Nenhum dado disponível
            </div>
        );
    }

    const total = data.reduce((acc, d) => acc + d.value, 0);

    const sortedData = [...data]
        .filter(d => d.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);

    const chartData = sortedData.map(d => ({ ...d, total }));

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        stroke="none"
                        label={labelRenderer}
                        labelLine={false}
                        onClick={(entry: any) => onSegmentClick?.(entry.name)}
                        style={{ cursor: onSegmentClick ? 'pointer' : 'default' }}
                    >
                        {chartData.map((entry, index) => {
                            const isSelected = selectedSegment === entry.name;
                            const dimmed = hasSelection && !isSelected;
                            return (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                                    opacity={dimmed ? 0.3 : 1}
                                    stroke={isSelected ? '#fff' : 'none'}
                                    strokeWidth={isSelected ? 2 : 0}
                                />
                            );
                        })}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 9999 }} />
                </PieChart>
            </ResponsiveContainer>

            {/* Total no centro */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none'
            }}>
                <div style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: 'var(--text-primary, #111827)',
                    lineHeight: 1.1
                }}>
                    {new Intl.NumberFormat('pt-BR', {
                        style: 'currency', currency: 'BRL',
                        notation: 'compact'
                    }).format(total)}
                </div>
                <div style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'var(--text-secondary, #6b7280)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                }}>
                    {selectedSegment ? selectedSegment : totalLabel}
                </div>
            </div>
        </div>
    );
};

export default LineDonutChart;
